import React from "react";
import {
  FaStar, FaTrophy, FaRocket,
  FaUsers, FaHeart, FaCertificate,
} from "react-icons/fa";
import MediaEmbed from "./shared/MediaEmbed";
import "./AssociativeExperience.css";

// ─── Data — sorted newest → oldest ────────────────────────────────────────
const EXPERIENCES = [
  {
    id: "pathfinder",
    icon: <FaRocket />,
    color: "#ffb840",
    role: "Co-Founder & Developer",
    organization: "PathFinder — Supernova National Startup Incubation Program",
    period: "2023 – 2024",
    location: "Tunis, Tunisia",
    description: "Co-founded PathFinder during Tunisia's national startup incubation programme. Built a web platform to help students discover and compare training centres and career programmes. Led the full product cycle — ideation, development, investor pitches, and prototype launch.",
    achievements: [
      "Completed Phase 1 of the Supernova national incubation programme",
      "Designed and built a functional platform within the programme timeline",
      "Led technical development while co-managing business strategy and investor pitches",
      "Navigated the full startup lifecycle from idea validation to working prototype",
    ],
    relatedCerts: [],
    certImg: null,
    pdfSrc: "/enterpreneurship/pathfinder.pdf",
    pdfLabel: "PathFinder Pitch Deck",
  },
  {
    id: "hult-committee",
    icon: <FaStar />,
    color: "#c87fff",
    role: "Judges & Experts Committee Member",
    organization: "Hult Prize at ISTIC — Organising Committee",
    period: "2023",
    location: "Tunis, Tunisia",
    description: "After competing as a finalist, was invited back as a committee member and judge for a subsequent edition of the Hult Prize at ISTIC — evaluating team pitches, providing structured feedback, and contributing to event organisation.",
    achievements: [
      "Evaluated and scored student team pitches as part of the expert jury",
      "Contributed to jury deliberation and scoring criteria design",
      "Provided constructive feedback to competing teams",
      "Supported full logistical organisation of the campus event",
    ],
    relatedCerts: ["Hult Prize Organising Committee Recognition"],
    certImg: "/enterpreneurship/hult%20prize%20organizing%20commetee%20member.jpeg",
    pdfSrc: null,
    pdfLabel: null,
  },
  {
    id: "hult-runnerup",
    icon: <FaTrophy />,
    color: "#ffca70",
    role: "2nd Runner-Up",
    organization: "Hult Prize at ISTIC",
    period: "2023",
    location: "Tunis, Tunisia",
    description: "Competed in the on-campus round of the global Hult Prize — one of the world's largest student social entrepreneurship competitions. The team developed a scalable social impact business model and earned 2nd Runner-Up at the ISTIC campus round.",
    achievements: [
      "Placed 2nd Runner-Up in Hult Prize at ISTIC on-campus round",
      "Developed a social impact business model addressing a community challenge",
      "Presented to a jury of entrepreneurs and academic experts under competitive conditions",
    ],
    relatedCerts: ["Hult Prize 2nd Runner-Up Certificate"],
    certImg: "/enterpreneurship/hult%20prize%20winning%20certif.jpeg",
    pdfSrc: null,
    pdfLabel: null,
  },
  {
    id: "universe-club",
    icon: <FaUsers />,
    color: "#88ffbb",
    role: "Event Management Member",
    organization: "Universe Club, ISTIC",
    period: "2022 – 2023",
    location: "Tunis, Tunisia",
    description: "Organised technology and culture events as part of ISTIC's main student association. Coordinated cross-functional volunteer teams, managed event logistics, and helped foster a vibrant campus community.",
    achievements: [
      "Co-organised multiple tech and culture campus events",
      "Coordinated teams of 10+ volunteers per event",
      "Gained hands-on experience in event logistics and team leadership",
    ],
    relatedCerts: [],
    certImg: null,
    pdfSrc: null,
    pdfLabel: null,
  },
  {
    id: "first-aid",
    icon: <FaHeart />,
    color: "#ff6666",
    role: "First Aid Training",
    organization: "Tunisian Red Crescent",
    period: "2022",
    location: "Tunis, Tunisia",
    description: "Completed certified first aid and emergency response training with the Tunisian Red Crescent. Trained in CPR, wound management, and emergency protocols.",
    achievements: [
      "Certified in first aid and emergency response procedures",
      "Trained in CPR and basic life support",
    ],
    relatedCerts: ["First Aid Certificate — Tunisian Red Crescent"],
    certImg: null,
    pdfSrc: null,
    pdfLabel: null,
  },
];

// ─── AssociativeExperience ────────────────────────────────────────────────
const AssociativeExperience = () => (
  <div className="associative-experience-section">
    <h2 className="section-title">Associations & Volunteering</h2>
    <p className="assoc-intro">
      Leadership, community, and social impact beyond the professional sphere — startup incubation, global entrepreneurship competitions, and student life.
    </p>

    {EXPERIENCES.map((exp) => (
      <div key={exp.id} className="assoc-card" style={{ "--assoc-color": exp.color }}>
        {/* Header row */}
        <div className="assoc-card__header">
          <div className="assoc-card__icon">{exp.icon}</div>
          <div className="assoc-card__info">
            <div className="assoc-card__role">{exp.role}</div>
            <div className="assoc-card__org">{exp.organization}</div>
            <div className="assoc-card__meta">
              <span>🗓 {exp.period}</span>
              <span>📍 {exp.location}</span>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="assoc-card__desc">{exp.description}</p>

        {/* Achievements */}
        <ul className="assoc-achievements">
          {exp.achievements.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>

        {/* Certificate image */}
        {exp.certImg && (
          <div className="assoc-cert-img-wrap">
            <img
              src={exp.certImg}
              alt={exp.relatedCerts[0] || "Certificate"}
              className="assoc-cert-img"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        )}

        {/* PDF embed */}
        {exp.pdfSrc && (
          <MediaEmbed
            src={exp.pdfSrc}
            type="pdf"
            label={exp.pdfLabel || "View presentation"}
          />
        )}

        {/* Related cert badges */}
        {exp.relatedCerts.length > 0 && (
          <div className="assoc-certs">
            {exp.relatedCerts.map((c, i) => (
              <span key={i} className="assoc-cert-badge">
                <FaCertificate /> {c}
              </span>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

export default AssociativeExperience;
